<template>
  <IntercomModal
    v-model:show="showModal"
    :title="vendor ? $t('vendors.editVendor') : $t('vendors.addVendor')"
    width="700px"
  >
    <div class="vendor-form">
      <!-- Section 1: Basic Info -->
      <IntercomInput
        v-model="formData.name"
        :label="$t('vendors.name')"
        :placeholder="$t('vendors.name')"
        required
        :error="errors.name"
      />

      <IntercomPhoneInput
        v-model="formData.phone"
        :label="$t('vendors.phone')"
        :placeholder="$t('vendors.phone')"
        :error="errors.phone"
        @update:valid="(v) => (phoneValid = v)"
      />

      <IntercomEmailInput
        v-model="formData.email"
        :label="$t('vendors.email')"
        :placeholder="$t('vendors.email')"
        :error="errors.email"
      />

      <IntercomInput
        v-model="formData.address"
        type="textarea"
        :label="$t('vendors.address')"
        :placeholder="$t('vendors.address')"
        :rows="3"
      />

      <!-- Section 2: Tax Details -->
      <div class="section-divider">{{ $t('vendors.taxDetails') }}</div>

      <IntercomInput
        v-model="formData.gstin"
        :label="$t('vendors.gstin')"
        :placeholder="$t('vendors.gstin')"
      />

      <IntercomInput
        v-model="formData.state_code"
        :label="$t('vendors.stateCode')"
        :placeholder="$t('vendors.stateCode')"
      />

      <!-- Section 3: Bank Details -->
      <div class="section-divider">{{ $t('vendors.bankDetails') }}</div>

      <div class="bank-grid">
        <IntercomInput
          v-model="formData.bank_name"
          :label="$t('vendors.bankName')"
          :placeholder="$t('vendors.bankName')"
        />

        <IntercomInput
          v-model="formData.bank_account_no"
          :label="$t('vendors.bankAccountNo')"
          :placeholder="$t('vendors.bankAccountNo')"
        />

        <IntercomInput
          v-model="formData.bank_branch"
          :label="$t('vendors.bankBranch')"
          :placeholder="$t('vendors.bankBranch')"
        />

        <IntercomInput
          v-model="formData.bank_ifsc"
          :label="$t('vendors.bankIfsc')"
          :placeholder="$t('vendors.bankIfsc')"
        />
      </div>

      <!-- Section 4: Balance -->
      <div class="section-divider">Opening Balance</div>

      <IntercomInput
        v-model="formData.opening_balance"
        label="Opening Balance"
        placeholder="Amount owed to this vendor before using OnePlaceHQ"
        type="number"
      />

      <IntercomInput
        v-model="formData.notes"
        type="textarea"
        :label="$t('vendors.notes')"
        :placeholder="$t('vendors.notes')"
        :rows="2"
      />
    </div>

    <template #footer>
      <IntercomButton variant="secondary" @click="handleCancel">
        {{ $t('common.cancel') }}
      </IntercomButton>
      <IntercomButton variant="primary" :loading="isLoading" @click="handleSubmit">
        {{ $t('common.save') }}
      </IntercomButton>
    </template>
  </IntercomModal>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  IntercomModal,
  IntercomInput,
  IntercomPhoneInput,
  IntercomEmailInput,
  IntercomButton,
} from '@shared/components/intercom'
import { useBilling } from '../composables/useBilling.js'

const props = defineProps({
  show: Boolean,
  vendor: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['update:show', 'success'])

const { t } = useI18n()
const { createVendor, updateVendor } = useBilling()

// State
const showModal = ref(props.show)
const isLoading = ref(false)
const phoneValid = ref(false)

// Form data
const formData = reactive({
  name: '',
  phone: '',
  email: '',
  address: '',
  gstin: '',
  state_code: '',
  bank_name: '',
  bank_account_no: '',
  bank_branch: '',
  bank_ifsc: '',
  notes: '',
  opening_balance: 0,
})

// Form errors
const errors = reactive({
  name: '',
  phone: '',
  email: '',
})

// Watch show prop
watch(
  () => props.show,
  (val) => {
    showModal.value = val
    if (val && props.vendor) {
      // Edit mode - populate form
      Object.assign(formData, {
        name: props.vendor.name,
        phone: props.vendor.phone || '',
        email: props.vendor.email || '',
        address: props.vendor.address || '',
        gstin: props.vendor.gstin || '',
        state_code: props.vendor.state_code || '',
        bank_name: props.vendor.bank_name || '',
        bank_account_no: props.vendor.bank_account_no || '',
        bank_branch: props.vendor.bank_branch || '',
        bank_ifsc: props.vendor.bank_ifsc || '',
        notes: props.vendor.notes || '',
        opening_balance: props.vendor.opening_balance || 0,
      })
    } else {
      // Create mode - reset form
      resetForm()
    }
    // Clear errors when opening
    clearErrors()
  }
)

// Watch modal state
watch(showModal, (val) => {
  emit('update:show', val)
})

/**
 * Clear errors
 */
const clearErrors = () => {
  errors.name = ''
  errors.phone = ''
  errors.email = ''
}

/**
 * Validate form
 */
const validateForm = () => {
  clearErrors()
  let isValid = true

  // Validate name
  if (!formData.name || formData.name.trim() === '') {
    errors.name = t('vendors.nameRequired')
    isValid = false
  }

  // Validate phone format if provided
  if (formData.phone && formData.phone.trim() !== '' && !phoneValid.value) {
    errors.phone = t('vendors.phoneInvalid')
    isValid = false
  }

  // Validate email format if provided
  if (formData.email && formData.email.trim() !== '') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      errors.email = t('vendors.emailInvalid')
      isValid = false
    }
  }

  return isValid
}

/**
 * Reset form
 */
const resetForm = () => {
  Object.assign(formData, {
    name: '',
    phone: '',
    email: '',
    address: '',
    gstin: '',
    state_code: '',
    bank_name: '',
    bank_account_no: '',
    bank_branch: '',
    bank_ifsc: '',
    notes: '',
    opening_balance: 0,
  })
  clearErrors()
}

/**
 * Handle cancel
 */
const handleCancel = () => {
  showModal.value = false
  resetForm()
}

/**
 * Handle submit
 */
const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  isLoading.value = true

  try {
    let phone = formData.phone || null
    if (phone && phone.startsWith('+91')) phone = phone.slice(3)

    const vendorData = {
      name: formData.name,
      phone: phone,
      email: formData.email || null,
      address: formData.address || null,
      gstin: formData.gstin || null,
      state_code: formData.state_code || null,
      bank_name: formData.bank_name || null,
      bank_account_no: formData.bank_account_no || null,
      bank_branch: formData.bank_branch || null,
      bank_ifsc: formData.bank_ifsc || null,
      notes: formData.notes || null,
      opening_balance: parseFloat(formData.opening_balance) || 0,
    }

    if (props.vendor) {
      await updateVendor(props.vendor.id, vendorData)
      console.log(t('vendors.updateSuccess'))
    } else {
      await createVendor(vendorData)
      console.log(t('vendors.createSuccess'))
    }

    showModal.value = false
    resetForm()
    emit('success')
  } catch (error) {
    console.error('Failed to save vendor:', error)
    // TODO: Show error notification
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.vendor-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.section-divider {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-500);
  margin: var(--space-4) 0 var(--space-2);
  padding-bottom: var(--space-2);
  border-bottom: 1px solid var(--color-border, #e5e7eb);
}

.bank-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
}
</style>
